import { Button, message, Table, Flex, Tag, Typography, Modal } from "antd";
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import AxiosService from "../services/APIService";
import { useEffect, useState } from "react";
import { LocalStorageService } from "../services/LocalStorage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="9" r="8.5" stroke="white" />
        <path d="M5.5 9L7.5 11L12.5 6" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const MyPurchase = () => {
    const apiService = new AxiosService();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [purchases, setPurchases] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [downloading, setDownloading] = useState(false);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [allPlans, setAllPlans] = useState([]);
    const [cartItems, setCartItems] = useState([]);

    const paginationLimit = 10;

    const userData = LocalStorageService.getItem("user");

    useEffect(() => {
        fetchPurchases(page);
    }, [page]);

    useEffect(() => {
        getAllPlans();
    }, []);

    const getAllPlans = async () => {
        try {
            const res = await apiService.get("/plan/all");
            if (res?.success) {
                setAllPlans(res.payload || res.data || []);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
        }
    };

    const handleAddToCart = (plan) => {
        // Since we are on MyPurchase page, we redirect to registration with the selected plan
        const userId = userData?._id || userData?.id;
        navigate(`/registration?planId=${plan._id}${userId ? `&userId=${userId}` : ''}`);
        setCartItems(prev => {
            const existing = prev.find(item => item._id === plan._id);
            if (existing) {
                return prev.map(item => item._id === plan._id ? { ...item, quantity: item.quantity + 1 } : item);
            }
            return [...prev, { ...plan, quantity: 1 }];
        });
        setIsPlanModalOpen(false);
    };

    const handleDownloadInvoice = async (purchase) => {
        try {
            setDownloading(true);

            const invoicePayload = {
                planName: purchase.planName,
                planPrice: purchase.planPrice,
                planQuantity: purchase.planQuantity,
                discountAmount: purchase.discountAmount,
                paidAmount: purchase.paidAmount,
                paymentMethod: purchase.paymentMethod,
                status: purchase.status,
                createdAt: purchase.createdAt,
                subscriptionId: purchase.subscriptionId,
            };

            const response = await apiService.post(`/payment/invoice`, invoicePayload);

            if (!response.success) {
                message.error("Failed to generate invoice");
                return;
            }

            const fileUrl = response.payload.invoiceUrl;

            window.open(fileUrl, "_blank");

            message.success("Invoice opened in new tab!");
        } catch (error) {
            console.error("Invoice error:", error);
            message.error("Failed to open invoice");
        } finally {
            setDownloading(false);
        }
    };

    const columns = [
        {
            title: "Plan Name",
            dataIndex: "planName",
            key: "planName",
        },
        {
            title: "Plan Price",
            dataIndex: "planPrice",
            key: "planPrice",
            render: (price) => `$${Number(price).toFixed(2)}`,
        },
        {
            title: "Quantity",
            dataIndex: "planQuantity",
            key: "planQuantity",
            render: (quantity) => quantity ? `${quantity}` : "-",
        },
        {
            title: "Discount",
            dataIndex: "discountAmount",
            key: "discountAmount",
            render: (discount) => discount ? `$${Number(discount).toFixed(2)}` : "-",
        },
        {
            title: "Paid Amount",
            dataIndex: "paidAmount",
            key: "paidAmount",
            render: (amount) => `$${Number(amount).toFixed(2)}`,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                let color = status === "PENDING" ? "orange" : status === "COMPLETED" ? "green" : "red";
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: "Payment Method",
            dataIndex: "paymentMethod",
            key: "paymentMethod",
        },
        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => dayjs.utc(date).format("DD-MM-YYYY"),
        },
        {
            title: "Invoice",
            key: "action",
            render: (_, record) => (
                <Button
                    onClick={() => handleDownloadInvoice(record)}
                    disabled={downloading}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#000",
                        border: "1px solid #d9d9d9"
                    }}
                >
                    {downloading ? (
                        <svg viewBox="0 0 1024 1024" focusable="false" width="1em" height="1em" fill="black" style={{ animation: 'spin 1s linear infinite' }}>
                            <style>
                                {`@keyframes spin { 100% { transform: rotate(360deg); } }`}
                            </style>
                            <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                        </svg>
                    ) : (
                        <svg
                            viewBox="64 64 896 896"
                            focusable="false"
                            data-icon="download"
                            width="1em"
                            height="1em"
                            fill="black"
                            aria-hidden="true"
                            style={{ fontSize: '20px', color: 'black' }}
                        >
                            <path d="M505.7 661a8 8 0 0012.6 0l112-141.7c4.1-5.2.4-12.9-6.3-12.9h-74.1V168c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v338.3H400c-6.7 0-10.4 7.7-6.3 12.9l112 141.8zM878 626h-60c-4.4 0-8 3.6-8 8v154H214V634c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v198c0 17.7 14.3 32 32 32h684c17.7 0 32-14.3 32-32V634c0-4.4-3.6-8-8-8z"></path>
                        </svg>
                    )}
                </Button>
            ),
        }
    ];

    const fetchPurchases = async (currentPage) => {
        setLoading(true);
        try {
            const response = await apiService.get(
                `/payment/purchases?page=${currentPage}&limit=${paginationLimit}`
            );

            if (response.success) {
                setPurchases(response.payload.purchases);
                setTotalCount(response.payload.count || 0);
            } else {
                message.error(response.message || "Failed to fetch purchases");
            }
        } catch (error) {
            message.error(error.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Typography.Title level={3} className="page-title">
                My Purchases
            </Typography.Title>
            <Flex justify='end' style={{ marginBottom: '15px' }}>
                <Button type="primary" onClick={() => setIsPlanModalOpen(true)}>
                    Purchase Plan
                </Button>
            </Flex>
            <Table
                pagination={{
                    pageSize: paginationLimit,
                    current: page,
                    total: totalCount,
                    onChange: (p) => setPage(p),
                }}
                loading={loading}
                columns={columns}
                dataSource={purchases}
                rowKey="_id"
            />

            <Modal
                title={null}
                footer={null}
                open={isPlanModalOpen}
                onCancel={() => setIsPlanModalOpen(false)}
                width={1200}
                centered
                styles={{ content: { padding: 0, background: 'transparent' } }}
                modalRender={(modal) => (
                    <div style={{ background: 'transparent' }}>
                        {modal}
                    </div>
                )}
            >
                <div className="prizingSectionAlignment" id="pricing" style={{
                    padding: '10px 40px',
                    backgroundColor: '#070707',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
                }}>
                    <div className="container-xs">
                        <div className="sectionTitle" style={{ padding: 0 }}>
                            <h2 style={{ margin: '0 auto 10px auto' }}>Discover Products With the Best Pricing</h2>
                        </div>

                        <div className="grid">
                            {allPlans.map((plan, index) => {
                                const mainFeatures = plan?.features?.slice(0, -2) || [];
                                const referFeatures = plan?.features?.slice(-2) || [];
                                // Calculate quantity of this plan in cart
                                const cartItem = cartItems.find(item => item._id === plan._id);
                                const quantityInCart = cartItem ? cartItem.quantity : 0;

                                return (
                                    <div key={index} className="griditems">
                                        {quantityInCart > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '12px',
                                                right: '12px',
                                                background: '#fff',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                color: '#000',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                                                zIndex: 20
                                            }}>
                                                {quantityInCart} Added to the Cart
                                            </div>
                                        )}
                                        <div className="mainrelative">
                                            <div className="leftRightSpacing" style={{ paddingTop: '15px' }}>
                                                <div className="boxHeaderAlignment" style={{ paddingBottom: '10px' }}>
                                                    <div>
                                                        <p style={{ marginTop: '15px' }}>{plan.name}</p>
                                                        <span style={{ display: 'block', minHeight: '42px', lineHeight: '1.3' }}>{plan.description || "Best for personal use."}</span>
                                                    </div>
                                                    <h3>${plan.price}</h3>
                                                </div>

                                                <div className="buttonDesign" style={{ marginBottom: '10px' }}>
                                                    <button onClick={() => {
                                                        handleAddToCart(plan);
                                                    }}>
                                                        Choose Plan
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M7 7H17V17" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M7 17L17 7" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                <div className="line" style={{ padding: '2px 0' }}>
                                                    <svg width="100%" height="2" viewBox="0 0 348 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M0 1L348 1.00003" stroke="url(#paint0_linear_line)" />
                                                        <defs>
                                                            <linearGradient id="paint0_linear_line" x1="0" y1="1.5" x2="348" y2="1.5" gradientUnits="userSpaceOnUse">
                                                                <stop stopColor="white" stopOpacity="0" />
                                                                <stop offset="0.5" stopColor="white" stopOpacity="0.5" />
                                                                <stop offset="1" stopColor="white" stopOpacity="0" />
                                                            </linearGradient>
                                                        </defs>
                                                    </svg>
                                                </div>

                                                <div className="details">
                                                    <h4>What you will get</h4>

                                                    <div className="iconText">
                                                        <CheckIcon />
                                                        <span>BUY AT ${plan.price}</span>
                                                    </div>

                                                    {/* Hardcoded fields mixed with dynamic features */}
                                                    {plan.liveAccountLicense && (
                                                        <div className="iconText">
                                                            <CheckIcon />
                                                            <span>{plan.liveAccountLicense} Live Account License</span>
                                                        </div>
                                                    )}
                                                    {plan.accountBalance && (
                                                        <div className="iconText">
                                                            <CheckIcon />
                                                            <span>Account Balance: {plan.accountBalance}</span>
                                                        </div>
                                                    )}
                                                    {plan.validity && (
                                                        <div className="iconText">
                                                            <CheckIcon />
                                                            <span>{plan.validity}</span>
                                                        </div>
                                                    )}
                                                    {plan.martingaleStrategy && (
                                                        <div className="iconText">
                                                            <CheckIcon />
                                                            <span>MARTINGALE STRATEGY</span>
                                                        </div>
                                                    )}

                                                    {/* Existing Dynamic Features (First Part) */}
                                                    {mainFeatures.map((item, i) => (
                                                        <div className="iconText" key={i}>
                                                            <CheckIcon />
                                                            <span>{item}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Referral Section (Bottom) */}
                                                <div className="newDetails">
                                                    <h6>Refer this Plan</h6>
                                                    <div className="lastTwoWrapper">
                                                        {referFeatures.length > 0 ? (
                                                            referFeatures.map((item, i) => (
                                                                <div className="iconText" key={`last-${i}`}>
                                                                    <CheckIcon />
                                                                    <span>{item}</span>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            // Fallback if no referral features exist
                                                            <>
                                                                <div className="iconText">
                                                                    <CheckIcon />
                                                                    <span>1 Strategy, 1 Currency Pair</span>
                                                                </div>
                                                                <div className="iconText">
                                                                    <CheckIcon />
                                                                    <span>Refer a friend and earn bonus</span>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MyPurchase;